import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AssignButtonNotConfigurable from 'bundles/unified-description-page-common/components/admin/AssignButtonNotConfigurable';
import EnterpriseCourseEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseCourseEnrollButtonNotConfigurable';
import HeroCoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/modules/hero/HeroCoBrandingPartnerLogos';
import HeroHorizontalModule from 'bundles/unified-description-page-common/components/modules/hero/HeroHorizontalModule';
import HeroInstructors from 'bundles/unified-description-page-common/components/modules/hero/HeroInstructors';
import HeroLanguageInfo from 'bundles/unified-description-page-common/components/modules/hero/HeroLanguageInfo';
import HeroModuleWithImage from 'bundles/unified-description-page-common/components/modules/hero/HeroModuleWithImage';
import HeroPartnerLogo from 'bundles/unified-description-page-common/components/modules/hero/HeroPartnerLogo';
import HeroTotalEnrollmentCount from 'bundles/unified-description-page-common/components/modules/hero/HeroTotalEnrollmentCount';
import HeroCourseDescription from 'bundles/unified-description-page-common/components/modules/hero/course/HeroCourseDescription';
import KeyInformationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalModule';
import KeyInformationSpecializationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationSpecializationHorizontalModule';
import ProductOverviewDescription from 'bundles/unified-description-page-common/components/modules/product-overview/Description';
import LearningObjectives from 'bundles/unified-description-page-common/components/modules/product-overview/LearningObjectives';
import ProductOverviewHorizontalModule from 'bundles/unified-description-page-common/components/modules/product-overview/ProductOverviewHorizontalModule';
import Skills from 'bundles/unified-description-page-common/components/modules/product-overview/Skills';
import Details from 'bundles/unified-description-page-common/components/modules/product-overview/details/Details';
import { HERO_BACKGROUND_COLOR } from 'bundles/unified-description-page-common/constants';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import AboutSubmoduleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/AboutSubmoduleRenderer';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import type { ProductOverviewModuleConfig } from 'bundles/unified-description-page-common/types/moduleConfig';
import { getSpecializationProductUrl } from 'bundles/unified-description-page-common/utils';
import getCourseProductDetailItems from 'bundles/unified-description-page-common/utils/getCourseProductDetailItems';
import getEnterpriseCourseKeyInformationItems from 'bundles/unified-description-page-common/utils/getEnterpriseCourseKeyInformationItems';
import { COURSE_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForCourse from 'bundles/unified-description-page-common/utils/getPartnersForCourse';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData: EnterpriseCoursePageFieldsFragment;
  config?: ProductOverviewModuleConfig;
};

export const EnterpriseCourseProductOverviewModuleRenderer = ({ pageData, config }: Props): React.ReactElement => {
  const { navigationLinks } = usePageNavigation();
  const productTitle = getProductTitle(pageData.name);
  const partnersToDisplay = getPartnersForCourse(pageData.partners, pageData.activeSpecialization);
  const { programSlug } = useParams();
  const features = useEnterpriseProductFeatures();

  const detailItems = getCourseProductDetailItems(pageData, config?.productDetailItems || []);

  const { items: keyInformationItems } = getEnterpriseCourseKeyInformationItems(pageData, {
    ...config?.keyInfoModule,
  });

  const overviewSkills = <Skills skills={pageData.skills} />;
  const overviewDescription = pageData.cmlLearningObjectives ? (
    <LearningObjectives learningObjectives={pageData.cmlLearningObjectives} />
  ) : (
    <ProductOverviewDescription description={pageData.description} productId={pageData.id} />
  );
  const details = <Details detailItems={detailItems} />;
  const showRedeisnedHorizontalModule = !!config?.showBackgroundImage;

  const HeroModuleComponent = showRedeisnedHorizontalModule ? HeroModuleWithImage : HeroHorizontalModule;
  const KeyInformationHorizontalComponent = showRedeisnedHorizontalModule
    ? KeyInformationSpecializationHorizontalModule
    : KeyInformationHorizontalModule;
  return (
    <ProductOverviewHorizontalModule
      anchorId={COURSE_NAV_ITEMS.ABOUT}
      heroBackgroundColor={HERO_BACKGROUND_COLOR}
      navigationLinks={navigationLinks}
      hero={
        <HeroModuleComponent
          productTitle={productTitle}
          {...(showRedeisnedHorizontalModule && {
            showBackgroundImage: true,
            backgroundImage: config?.backgroundImage,
          })}
          partnerLogo={
            partnersToDisplay?.length === 2 ? (
              <HeroCoBrandingPartnerLogos
                // @ts-expect-error URL: string
                leftImage={partnersToDisplay[0].primaryLogo}
                leftPartnerName={partnersToDisplay[0].name}
                // @ts-expect-error URL: string
                rightImage={partnersToDisplay[1].primaryLogo}
                rightPartnerName={partnersToDisplay[1].name}
              />
            ) : (
              // @ts-expect-error URL: string
              <HeroPartnerLogo image={partnersToDisplay?.[0].primaryLogo} partnerName={partnersToDisplay?.[0].name} />
            )
          }
          description={
            <React.Fragment>
              {pageData.primarySpecialization && (
                <HeroCourseDescription
                  primarySpecialization={pageData.primarySpecialization}
                  primarySpecializationLink={getSpecializationProductUrl({
                    productSlug: pageData.primarySpecialization.slug,
                    productVariant: pageData.primarySpecialization.productVariant,
                  })}
                  specializations={pageData.specializations}
                />
              )}
              {config?.showHeroLanguageInfo && (
                <HeroLanguageInfo
                  languages={{
                    primaryLanguages: pageData.primaryLanguages,
                    subtitleLanguages: pageData.subtitleLanguages ?? [],
                    translatedLanguages: pageData.translatedLanguages ?? [],
                  }}
                />
              )}
            </React.Fragment>
          }
          instructors={
            <HeroInstructors
              instructors={pageData.instructors}
              averageInstructorRating={pageData.averageInstructorRating}
              instructorRatingCount={pageData.instructorRatingCount}
              partners={pageData.partners}
              disableCTA={config?.removeLinksAboveFold}
              displayTopInstructorBadgeInline
            />
          }
          primaryCta={
            <EnterpriseCourseEnrollButtonNotConfigurable
              courseType={pageData.courseType}
              productId={pageData.id}
              slug={pageData.slug}
              shouldShowSponsoredByMessage={features?.shouldShowSponsoredByMessage}
              // eslint-disable-next-line no-restricted-syntax
              buttonLabel={_t('Go To Course')}
            />
          }
          secondaryCta={
            config?.showAssignButton && (
              <AssignButtonNotConfigurable productSlug={pageData.slug} programSlug={programSlug} productType="course" />
            )
          }
          additionalInfo={<HeroTotalEnrollmentCount count={pageData.totalEnrollmentCount} />}
        />
      }
      keyInformation={<KeyInformationHorizontalComponent keyInformationItems={keyInformationItems} />}
      aboutSubmodule={
        <AboutSubmoduleRenderer
          skills={overviewSkills}
          description={overviewDescription}
          details={details}
          config={config}
        />
      }
    />
  );
};

export default EnterpriseCourseProductOverviewModuleRenderer;
